import React, {
    useState,
    useRef,
    useEffect,
    useLayoutEffect,
    useCallback,
    useMemo,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoCheckmarkDoneSharp, IoCheckmarkSharp } from "react-icons/io5";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../lib/api.js";
import { useSocket } from "../lib/useSocket.js";
import { useStorePresence } from "../lib/useStorePresence.js";
import { CallOverlay } from "./CallOverlay.jsx";
import { products } from "salesive-api-axios";
import {
    hasSession,
    isLoggedIn as checkIsLoggedIn,
    getLocalAuthMode,
} from "../lib/sessionState.js";
import {
    ensureShopperSession,
    syncSessionFromServer,
} from "../lib/sessionSync.js";
import { useSalesiveConfig } from "salesive-dev-tools";
import { ConversationList } from "./chat/ConversationList.jsx";
import { AuthForm } from "./chat/AuthForm.jsx";
import { useProductMention } from "./chat/useProductMention.js";
import { ProductMentionDropdown } from "./chat/ProductMentionDropdown.jsx";
import { ImageViewer } from "./chat/ImageViewer.jsx";
import { MessageMarkdown } from "./chat/MessageMarkdown.jsx";
import { ProductCardList } from "./chat/ProductCardList.jsx";
import { CopiableText } from "./chat/CopiableText.jsx";
import { ButtonLink } from "./chat/ButtonLink.jsx";

import {
    extractMessages,
    extractConversations,
    normalizeMessage,
    normalizeConversation,
    getConversationLabel,
    getMessageConversationId,
    getPersistedConversationId,
    persistConversationId,
    playNotificationSound,
    createClientMessageId,
    formatRelativeTime,
    isCustomerMessage,
    isShopperTypingUser,
} from "./chat/chatUtils.js";

/**
 * ChatWindow Component
 * Main chat interface with message history and input
 */
export function ChatWindow({
    isOpen,
    onClose,
    darkMode = false,
    onUnreadCountChange,
    onPrefillReady,
    onCallApiReady,
    call,
}) {
    const queryClient = useQueryClient();
    const [selectedConversationId, setSelectedConversationId] = useState(null);
    const [showConversationList, setShowConversationList] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [error, setError] = useState(null);
    const [isSessionReady, setIsSessionReady] = useState(hasSession());
    const [isLoggedIn, setIsLoggedIn] = useState(checkIsLoggedIn());
    const [hasSkippedAuth, setHasSkippedAuth] = useState(false);
    const wasLoggedInRef = useRef(checkIsLoggedIn());
    const sessionSyncInFlightRef = useRef(null);
    const [typingUsers, setTypingUsers] = useState({}); // { [conversationId]: user }
    const typingTimersRef = useRef({}); // auto-clear typing after timeout
    const [pendingMessages, setPendingMessages] = useState([]);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const logo = useSalesiveConfig("logo");

    const [currentShop, setCurrentShop] = useState(null);
    // { file, localUrl, uploadedUrl, uploading, error }
    const [imagePreview, setImagePreview] = useState(null);
    const [viewerImageIndex, setViewerImageIndex] = useState(-1); // index into chatImages
    const fileInputRef = useRef(null);
    const persistedConversationIdRef = useRef(getPersistedConversationId());
    const previousMessagesLengthRef = useRef(0);
    const hasScrolledCurrentConversationRef = useRef(false);
    const wasNearBottomRef = useRef(true);
    const hasOpenedOnceRef = useRef(false);
    const messagesContainerRef = useRef(null);
    // Track container mount via callback ref to handle AnimatePresence remounts
    const [containerMounted, setContainerMounted] = useState(0);
    const messagesContainerCallbackRef = useCallback((node) => {
        messagesContainerRef.current = node;
        if (node) setContainerMounted((c) => c + 1); // trigger effect re-run
    }, []);
    const isNearBottom = useCallback((container, threshold = 96) => {
        if (!container) return true;
        return (
            container.scrollHeight - container.scrollTop - container.clientHeight
        ) <= threshold;
    }, []);

    // ─── Conversations query ─────────────────────────────────────────
    const { data: conversations = [], isLoading: isConversationsLoading } =
        useQuery({
            queryKey: ["conversations"],
            queryFn: async () => {
                const data = await api.fetchConversations({
                    type: "shop_support",
                    limit: 20,
                });

                // Extract currentShop from the response
                const shop =
                    data?.data?.currentShop ||
                    data?.currentShop ||
                    null;
                if (shop) setCurrentShop(shop);

                let list = extractConversations(data)
                    .map(normalizeConversation)
                    .filter(Boolean);

                // Ensure there's a conversation for the current store
                const shopId = shop?._id;
                const hasCurrentStoreConvo = shopId
                    ? list.some(
                          (c) =>
                              String(c.shop?._id || c.shop?.id) ===
                              String(shopId),
                      )
                    : list.length > 0;

                if (!hasCurrentStoreConvo) {
                    const created = await api.createConversation({
                        type: "shop_support",
                    });
                    const createdConversation = normalizeConversation(
                        created?.data || created?.conversation || created,
                    );
                    if (createdConversation) {
                        list = [createdConversation, ...list];
                    }
                }
                return list;
            },
            enabled: isSessionReady,
            staleTime: 30_000,
            refetchOnWindowFocus: false,
        });

    // ─── Messages query ──────────────────────────────────────────────
    const {
        data: serverMessages = [],
        isLoading: isMessagesLoading,
        isFetched: hasLoadedHistory,
    } = useQuery({
        queryKey: ["messages", selectedConversationId],
        queryFn: async () => {
            const data = await api.fetchConversationMessages(
                selectedConversationId,
                { limit: 50 },
            );
            const raw = extractMessages(data);
            const normalized = raw.map(normalizeMessage).filter(Boolean);
            return normalized;
        },
        enabled: isSessionReady && Boolean(selectedConversationId),
        staleTime: 30_000,
        refetchOnWindowFocus: false,
    });

    // Merge server messages + pending (optimistic) messages
    const messages = useMemo(() => {
        const map = new Map();
        // Index server messages by both id and clientMessageId for dedup
        const confirmedClientIds = new Set();
        serverMessages.forEach((msg) => {
            map.set(msg.id || msg.clientMessageId, msg);
            if (msg.clientMessageId) confirmedClientIds.add(msg.clientMessageId);
        });
        // Only add pending messages that haven't been confirmed by the server
        pendingMessages.forEach((msg) => {
            const key = msg.clientMessageId || msg.id;
            if (!map.has(key) && !confirmedClientIds.has(msg.clientMessageId)) {
                map.set(key, msg);
            }
        });
        return Array.from(map.values()).sort(
            (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
        );
    }, [serverMessages, pendingMessages]);

    const isLoading = isMessagesLoading;

    // Collect all image URLs from messages for the viewer
    const chatImages = useMemo(() => {
        return messages
            .filter((m) => m.messageType === "image" && (m.attachment?.url || m.localImageUrl))
            .map((m) => ({
                src: m.attachment?.url || m.localImageUrl,
                alt: m.attachment?.name || "Image",
                messageId: m.clientMessageId || m.id,
            }));
    }, [messages]);

    // Typing user for the currently selected conversation
    const typingUser = selectedConversationId
        ? typingUsers[selectedConversationId] || null
        : null;

    const canSend =
        (Boolean(inputValue.trim()) || (imagePreview && imagePreview.uploadedUrl)) &&
        !(imagePreview?.uploading) &&
        isSessionReady &&
        Boolean(selectedConversationId) &&
        !isConversationsLoading;

    // Check if selected conversation belongs to the current shop
    const isCurrentShopConversation = useMemo(() => {
        if (!currentShop?._id || !selectedConversationId || !conversations.length) return false;
        const convo = conversations.find((c) => String(c.id) === String(selectedConversationId));
        return convo ? String(convo.shop?._id || convo.shop?.id) === String(currentShop._id) : false;
    }, [currentShop, selectedConversationId, conversations]);

    // ─── Voice call gating ───────────────────────────────────────────
    // The call button shows when the shop enables voice calls (server-
    // authoritative flag on the conversations `currentShop` payload) and the
    // open conversation belongs to this shop. Visitors may call even when no
    // staff is currently online — the server rings the store via push and, if
    // no one answers, logs it as a missed call.
    const canStartCall =
        Boolean(call) &&
        currentShop?.voiceCallsEnabled === true &&
        isCurrentShopConversation;
    // Presence drives only a subtle hint on the call button (NOT whether it
    // shows) so visitors know the team may be away before they call.
    const isStoreOnline = useStorePresence();

    const handleStartCall = useCallback(() => {
        if (!call || call.status !== "idle" || !selectedConversationId) return;
        call.startCall(selectedConversationId, {
            name: currentShop?.name || "Support",
            avatar: currentShop?.logo || null,
        });
    }, [call, selectedConversationId, currentShop]);

    // The public widget API (SalesiveWidget.chat.call.start()) lands here — this
    // component owns the conversation context a call needs. Registered
    // prefill-style so the API works whenever the window is mounted, and
    // reports WHY when it can't start rather than failing silently.
    const startCallFromApi = useCallback(() => {
        if (currentShop && currentShop.voiceCallsEnabled !== true) {
            return { started: false, reason: "disabled" };
        }
        if (!canStartCall) {
            return {
                started: false,
                reason: !currentShop
                    ? "loading"
                    : call?.status !== "idle"
                      ? "busy"
                      : "unavailable",
            };
        }
        handleStartCall();
        return { started: true };
    }, [canStartCall, currentShop, call, handleStartCall]);

    useEffect(() => {
        if (typeof onCallApiReady === "function") {
            onCallApiReady({
                start: startCallFromApi,
                get enabled() {
                    return currentShop
                        ? currentShop.voiceCallsEnabled === true
                        : null;
                },
            });
            return () => onCallApiReady(null);
        }
        return undefined;
    }, [onCallApiReady, startCallFromApi, currentShop]);

    // Product @mention — only enabled for current shop conversations
    const productMention = useProductMention({
        inputValue,
        setInputValue,
        inputRef,
        enabled: isCurrentShopConversation,
    });

    const {
        disconnect,
        connect,
        joinConversation,
        fetchHistory: fetchSocketHistory,
        sendCustomerMessage,
        markConversationRead,
        joinStore,
        onMessageNew,
        onMessageSent,
        onMessageHistory,
        onMessageRead,
        onMessageError,
        onMessageJoined,
        onStoreJoined,
        onStoreMessageNew,
        // onMessageTyping, // Removed as it is not in useSocket
        onTypingStart,
        onTypingStop,
        startTyping,
        stopTyping,
    } = useSocket();

    useEffect(() => {
        let isMounted = true;

        async function ensureSession() {
            try {
                const mode = await ensureShopperSession();
                if (!isMounted) return;
                setIsSessionReady(true);
                const loggedIn = mode === "user";
                wasLoggedInRef.current = loggedIn;
                setIsLoggedIn(loggedIn);
            } catch (sessionError) {
                console.error(
                    "[SalesiveWidget] Failed to create session",
                    sessionError,
                );
                if (isMounted) {
                    setError(
                        "Unable to start chat session. Please refresh and try again.",
                    );
                }
            }
        }

        ensureSession();

        return () => {
            isMounted = false;
        };
    }, []);

    // Keep auth UI in step with the store cookie for the whole visit.
    //
    // The storefront is an SPA: shoppers sign in / out without a full reload, the
    // widget never remounts, and the httpOnly `jwt` cookie is invisible to JS. The
    // only reliable signal is `auth.session()` on the store API. We poll that on a
    // short interval and also on focus / visibility / storage changes so a theme
    // logout immediately flips the widget back to the Welcome Back (ghost) screen.
    const applyLoggedInChange = useCallback(
        (loggedIn) => {
            if (hasSession()) {
                setIsSessionReady(true);
            }

            if (loggedIn === wasLoggedInRef.current) {
                setIsLoggedIn(loggedIn);
                return;
            }

            wasLoggedInRef.current = loggedIn;
            setIsLoggedIn(loggedIn);

            // Returning to ghost after a theme logout should offer sign-in again.
            if (!loggedIn) {
                setHasSkippedAuth(false);
            }

            // Socket is still the previous identity — reconnect so conversations
            // attach to the account (or ghost) that owns the cookie now.
            disconnect();
            connect();
            queryClient.invalidateQueries({ queryKey: ["conversations"] });
        },
        [disconnect, connect, queryClient],
    );

    const pendingServerSyncRef = useRef(false);

    const syncAuthState = useCallback(
        async ({ forceServer = false } = {}) => {
            // Cheap path: if we already mirrored a user session and nothing forced
            // a server check, trust the mirror for the tick.
            if (!forceServer) {
                const loggedIn = checkIsLoggedIn();
                if (loggedIn === wasLoggedInRef.current) {
                    if (hasSession()) setIsSessionReady(true);
                    return;
                }
            }

            // If a sync is already in flight, queue one more after it finishes
            // so a logout that happens mid-request is not lost to a stale USER
            // response applying after the cookie was cleared.
            if (sessionSyncInFlightRef.current) {
                pendingServerSyncRef.current = true;
                return sessionSyncInFlightRef.current;
            }

            sessionSyncInFlightRef.current = (async () => {
                try {
                    let mode = await syncSessionFromServer();
                    if (mode === "none") {
                        mode = await ensureShopperSession();
                    }
                    // Drive UI from the server mode directly — never let a stale
                    // localStorage USER win after the cookie is gone.
                    applyLoggedInChange(mode === "user");
                } catch (err) {
                    console.warn(
                        "[SalesiveWidget] Session sync failed",
                        err,
                    );
                    // On hard failure still drop to logged-out UI if local
                    // artifacts were cleared by a theme logout.
                    applyLoggedInChange(checkIsLoggedIn());
                } finally {
                    sessionSyncInFlightRef.current = null;
                    if (pendingServerSyncRef.current) {
                        pendingServerSyncRef.current = false;
                        syncAuthState({ forceServer: true });
                    }
                }
            })();

            return sessionSyncInFlightRef.current;
        },
        [applyLoggedInChange],
    );

    useEffect(() => {
        const onServerSync = () => {
            syncAuthState({ forceServer: true });
        };

        // Immediate check once mounted, then periodic + event-driven refreshes.
        onServerSync();
        const interval = setInterval(onServerSync, 2000);
        window.addEventListener("storage", onServerSync);
        window.addEventListener("focus", onServerSync);
        document.addEventListener("visibilitychange", onServerSync);

        // Same-tab logout: the `storage` event does not fire in the document
        // that wrote the value. The theme clears SALESIVE_AUTH_MODE on logout,
        // so poll it and force a server sync when it changes.
        let lastAuthMode = getLocalAuthMode();
        const localAuthInterval = setInterval(() => {
            const mode = getLocalAuthMode();
            if (mode !== lastAuthMode) {
                lastAuthMode = mode;
                onServerSync();
            }
        }, 400);

        return () => {
            clearInterval(interval);
            clearInterval(localAuthInterval);
            window.removeEventListener("storage", onServerSync);
            window.removeEventListener("focus", onServerSync);
            document.removeEventListener("visibilitychange", onServerSync);
        };
    }, [syncAuthState]);

    // Auto-select conversation when data arrives
    useEffect(() => {
        if (!conversations.length) return;

        setSelectedConversationId((prev) => {
            if (
                prev &&
                conversations.some((c) => String(c.id) === String(prev))
            ) {
                return prev; // keep current selection
            }
            const persisted = persistedConversationIdRef.current;
            if (
                persisted &&
                conversations.some((c) => String(c.id) === String(persisted))
            ) {
                setShowConversationList(false);
                return persisted;
            }
            if (conversations.length === 1) {
                setShowConversationList(false);
                return conversations[0].id;
            }
            // Multiple convos, no persisted — default to the current store's convo
            if (conversations.length > 1 && currentShop?._id) {
                const currentStoreConvo = conversations.find(
                    (c) =>
                        String(c.shop?._id || c.shop?.id) ===
                        String(currentShop._id),
                );
                if (currentStoreConvo) {
                    setShowConversationList(false);
                    return currentStoreConvo.id;
                }
            }
            if (conversations.length > 1) {
                setShowConversationList(true);
            }
            return null;
        });
    }, [conversations, currentShop]);

    useEffect(() => {
        if (!selectedConversationId) return;
        persistConversationId(selectedConversationId);
    }, [selectedConversationId]);

    const totalUnread = conversations.reduce(
        (sum, c) => sum + (Number(c.unreadCount) || 0),
        0,
    );

    useEffect(() => {
        if (typeof window === "undefined") return;
        if (typeof onUnreadCountChange === "function") {
            onUnreadCountChange(totalUnread);
        }
    }, [totalUnread, onUnreadCountChange]);

    // Reset pending messages and scroll state when conversation changes
    useEffect(() => {
        setPendingMessages([]);
        setError(null);
        previousMessagesLengthRef.current = 0;
        hasScrolledCurrentConversationRef.current = false;
        wasNearBottomRef.current = true;
    }, [selectedConversationId]);

    // Helper: merge incoming messages into the messages query cache
    const mergeMessagesIntoCache = useCallback(
        (incoming, conversationId) => {
            const list = extractMessages(incoming)
                .map(normalizeMessage)
                .filter(Boolean);
            if (!list.length) return;

            queryClient.setQueryData(
                ["messages", conversationId || selectedConversationId],
                (prev = []) => {
                    const map = new Map();
                    prev.forEach((msg) =>
                        map.set(msg.id || msg.clientMessageId, msg),
                    );
                    list.forEach((msg) =>
                        map.set(msg.id || msg.clientMessageId, msg),
                    );
                    return Array.from(map.values()).sort(
                        (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
                    );
                },
            );

            // Remove any pending messages that are now confirmed
            setPendingMessages((pending) =>
                pending.filter(
                    (p) =>
                        !list.some(
                            (m) =>
                                m.clientMessageId &&
                                m.clientMessageId === p.clientMessageId,
                        ),
                ),
            );
        },
        [queryClient, selectedConversationId],
    );

    // Helper: update a pending message with server response
    const replacePendingMessage = useCallback(
        (resolvedMessage) => {
            // Move from pending to query cache
            const normalized = { ...resolvedMessage, pending: false };
            queryClient.setQueryData(
                ["messages", selectedConversationId],
                (prev = []) => {
                    const exists = prev.some(
                        (m) =>
                            m.id === normalized.id ||
                            (m.clientMessageId &&
                                m.clientMessageId ===
                                    normalized.clientMessageId),
                    );
                    if (exists) {
                        return prev.map((m) =>
                            (normalized.clientMessageId &&
                                m.clientMessageId ===
                                    normalized.clientMessageId) ||
                            m.id === normalized.id
                                ? { ...m, ...normalized }
                                : m,
                        );
                    }
                    return [...prev, normalized].sort(
                        (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
                    );
                },
            );
            // Remove from pending
            setPendingMessages((pending) =>
                pending.filter(
                    (p) =>
                        !(
                            normalized.clientMessageId &&
                            p.clientMessageId === normalized.clientMessageId
                        ),
                ),
            );
        },
        [queryClient, selectedConversationId],
    );

    // Helper: update conversations cache
    const updateConversationsCache = useCallback(
        (updater) => {
            queryClient.setQueryData(["conversations"], (prev = []) =>
                updater(prev),
            );
        },
        [queryClient],
    );

    // Helper: mark a conversation as online when we detect agent activity
    const markConversationOnline = useCallback(
        (conversationId) => {
            if (!conversationId) return;
            updateConversationsCache((prev) =>
                prev.map((c) =>
                    String(c.id) === String(conversationId) && !c.online
                        ? { ...c, online: true }
                        : c,
                ),
            );
        },
        [updateConversationsCache],
    );

    // Prevent scroll propagation to main page when at chat boundaries
    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container) return;

        const handleWheel = (e) => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const atTop = scrollTop <= 0;
            const atBottom = scrollTop + clientHeight >= scrollHeight - 1; // -1 for rounding tolerance

            if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        const handleTouchStart = (e) => {
            container._touchStartY = e.touches[0].clientY;
        };

        const handleTouchMove = (e) => {
            if (container._touchStartY == null) return;

            const touchY = e.touches[0].clientY;
            const deltaY = container._touchStartY - touchY;
            const { scrollTop, scrollHeight, clientHeight } = container;
            const atTop = scrollTop <= 0;
            const atBottom = scrollTop + clientHeight >= scrollHeight - 1;

            if ((deltaY < 0 && atTop) || (deltaY > 0 && atBottom)) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        container.addEventListener("wheel", handleWheel, { passive: false });
        container.addEventListener("touchstart", handleTouchStart, {
            passive: true,
        });
        container.addEventListener("touchmove", handleTouchMove, {
            passive: false,
        });

        return () => {
            container.removeEventListener("wheel", handleWheel);
            container.removeEventListener("touchstart", handleTouchStart);
            container.removeEventListener("touchmove", handleTouchMove);
        };
    }, [isOpen]);

    // Handle mobile keyboard - scroll input into view and adjust layout
    useEffect(() => {
        if (!isOpen) return;

        const viewport = window.visualViewport;
        if (!viewport) return;

        const handleResize = () => {
            // When keyboard opens, scroll the input into view
            if (
                inputRef.current &&
                document.activeElement === inputRef.current
            ) {
                setTimeout(() => {
                    inputRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                }, 100);
            }
        };

        viewport.addEventListener("resize", handleResize);
        return () => viewport.removeEventListener("resize", handleResize);
    }, [isOpen]);

    // Scroll helper
    const scrollToBottom = useCallback((smooth = false) => {
        const container = messagesContainerRef.current;
        if (!container) return;
        if (smooth) {
            container.scrollTo({
                top: container.scrollHeight,
                behavior: "smooth",
            });
        } else {
            container.scrollTop = container.scrollHeight;
        }
    }, []);

    // Persist scroll position to sessionStorage
    const SCROLL_STORAGE_KEY = "salesive_scroll_pos";

    const saveScrollPosition = useCallback(() => {
        const container = messagesContainerRef.current;
        if (!container || !selectedConversationId) return;
        try {
            const positions = JSON.parse(
                sessionStorage.getItem(SCROLL_STORAGE_KEY) || "{}",
            );
            positions[selectedConversationId] = container.scrollTop;
            sessionStorage.setItem(
                SCROLL_STORAGE_KEY,
                JSON.stringify(positions),
            );
        } catch {}
    }, [selectedConversationId]);

    const getSavedScrollPosition = useCallback(() => {
        if (!selectedConversationId) return null;
        try {
            const positions = JSON.parse(
                sessionStorage.getItem(SCROLL_STORAGE_KEY) || "{}",
            );
            const pos = positions[selectedConversationId];
            return pos != null ? pos : null;
        } catch {
            return null;
        }
    }, [selectedConversationId]);

    // Save scroll position on scroll and when widget closes
    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container) return;
        let ticking = false;
        const onScroll = () => {
            if (!ticking) {
                ticking = true;
                requestAnimationFrame(() => {
                    wasNearBottomRef.current = isNearBottom(container);
                    saveScrollPosition();
                    ticking = false;
                });
            }
        };
        wasNearBottomRef.current = isNearBottom(container);
        container.addEventListener("scroll", onScroll, { passive: true });
        return () => container.removeEventListener("scroll", onScroll);
    }, [isNearBottom, saveScrollPosition, isOpen, selectedConversationId]);

    // Save when widget closes
    useEffect(() => {
        if (!isOpen) saveScrollPosition();
    }, [isOpen, saveScrollPosition]);

    // Set scroll position synchronously before the browser paints.
    // The container starts at opacity 0 (set via DOM ref, not state) and is
    // revealed only after scrollTop is positioned correctly.
    useLayoutEffect(() => {
        if (!isOpen) return;
        const container = messagesContainerRef.current;
        if (!container) return;

        // Nothing loaded yet — show container as-is (loading state / empty)
        if (!hasLoadedHistory || messages.length === 0) {
            container.style.opacity = "1";
            wasNearBottomRef.current = true;
            return;
        }

        if (!hasScrolledCurrentConversationRef.current) {
            const saved = getSavedScrollPosition();
            const useBottom = saved == null || saved === 0;
            container.scrollTop = useBottom ? container.scrollHeight : saved;
            hasScrolledCurrentConversationRef.current = true;
            hasOpenedOnceRef.current = true;
        } else if (wasNearBottomRef.current) {
            container.scrollTop = container.scrollHeight;
        }

        container.style.opacity = "1";
        wasNearBottomRef.current = isNearBottom(container);
    }, [
        isNearBottom,
        isOpen,
        hasLoadedHistory,
        messages.length,
        selectedConversationId,
        getSavedScrollPosition,
        containerMounted,
    ]);

    // Auto-scroll on new messages
    useEffect(() => {
        const currentLength = messages.length;
        const previousLength = previousMessagesLengthRef.current;
        previousMessagesLengthRef.current = currentLength;

        if (currentLength <= previousLength || previousLength === 0) return;

        const lastMessage = messages[currentLength - 1];
        const isMyMessage = lastMessage?.type === "user";
        const shouldStickToBottom = isMyMessage || wasNearBottomRef.current;

        if (!shouldStickToBottom) return;

        requestAnimationFrame(() => {
            scrollToBottom(isMyMessage);
            requestAnimationFrame(() => scrollToBottom(false));
        });
    }, [messages, scrollToBottom]);

    // Scroll to bottom when typing indicator appears
    useEffect(() => {
        if (typingUser && isOpen) {
            setTimeout(() => scrollToBottom(true), 50);
        }
    }, [typingUser, isOpen, scrollToBottom]);

    const handleSend = useCallback(() => {
        const hasText = Boolean(inputValue.trim());
        const hasImage = imagePreview && !imagePreview.uploading && imagePreview.uploadedUrl;
        if (!hasText && !hasImage) return;
        if (!isSessionReady) {
            setError("Initializing chat session. Please wait a moment…");
            return;
        }
        if (!selectedConversationId) {
            setError("Please select a conversation and try again.");
            return;
        }

        // Stop typing indicator immediately when sending
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
            isTypingRef.current = false;
            stopTyping(selectedConversationId);
        }

        const clientMessageId = createClientMessageId();
        const text = inputValue.trim();
        const currentImage = hasImage ? imagePreview : null;

        // Build attachment from pre-uploaded image
        let attachment = null;
        let msgType = "text";
        if (currentImage) {
            msgType = "image";
            attachment = {
                url: currentImage.uploadedUrl,
                name: currentImage.file.name,
                size: currentImage.file.size,
                mimeType: currentImage.file.type,
            };
        }

        const pendingMessage = {
            id: clientMessageId,
            clientMessageId,
            type: "user",
            messageType: msgType,
            text,
            attachment,
            localImageUrl: currentImage?.localUrl || null,
            timestamp: new Date(),
            pending: true,
        };

        // Clear input immediately
        setPendingMessages((prev) => [...prev, pendingMessage]);
        setInputValue("");
        if (currentImage?.localUrl) URL.revokeObjectURL(currentImage.localUrl);
        setImagePreview(null);
        setError(null);

        // Update conversations cache with the new message
        const previewContent = msgType === "image" ? (text || "📷 Photo") : text;
        updateConversationsCache((prev) =>
            prev.map((c) =>
                String(c.id) === String(selectedConversationId)
                    ? {
                          ...c,
                          lastMessage: {
                              ...(c.lastMessage || {}),
                              content: previewContent,
                              createdAt: pendingMessage.timestamp,
                          },
                          updatedAt: pendingMessage.timestamp,
                      }
                    : c,
            ),
        );
        queryClient.invalidateQueries({ queryKey: ["conversations"] });

        // Force scroll to bottom immediately after sending
        setTimeout(() => {
            const container = messagesContainerRef.current;
            if (container) {
                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: "smooth",
                });
            }
        }, 50);

        sendCustomerMessage(
            {
                conversationId: selectedConversationId,
                content: text,
                clientMessageId,
                type: msgType,
                ...(attachment ? { attachment } : {}),
            },
            (response) => {
                console.log("[SalesiveWidget] message:send callback", response);

                if (!response) {
                    // No response — remove bubble, restore input
                    setError("Failed to send message. Please try again.");
                    setPendingMessages((prev) =>
                        prev.filter((p) => p.clientMessageId !== clientMessageId),
                    );
                    queryClient.setQueryData(
                        ["messages", selectedConversationId],
                        (prev = []) => prev.filter(
                            (m) => m.clientMessageId !== clientMessageId && m.id !== clientMessageId,
                        ),
                    );
                    setInputValue(text);
                    if (currentImage) {
                        setImagePreview(currentImage);
                    }
                    return;
                }

                if (response.success === false || response.error) {
                    setError(
                        response.message ||
                            response.error ||
                            "Failed to send message",
                    );
                    // Remove the pending bubble and restore input
                    setPendingMessages((prev) =>
                        prev.filter((p) => p.clientMessageId !== clientMessageId),
                    );
                    queryClient.setQueryData(
                        ["messages", selectedConversationId],
                        (prev = []) => prev.filter(
                            (m) => m.clientMessageId !== clientMessageId && m.id !== clientMessageId,
                        ),
                    );
                    setInputValue(text);
                    if (currentImage) {
                        setImagePreview(currentImage);
                    }
                    return;
                }

                const normalized = normalizeMessage(response.data || response);
                if (normalized) {
                    replacePendingMessage({
                        ...normalized,
                        clientMessageId,
                    });
                } else {
                    replacePendingMessage({
                        ...pendingMessage,
                        pending: false,
                    });
                }
            },
        );
    }, [
        inputValue,
        imagePreview,
        isSessionReady,
        selectedConversationId,
        sendCustomerMessage,
        replacePendingMessage,
        stopTyping,
        queryClient,
    ]);

    const handleImageSelect = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            setError("Please select an image file.");
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setError("Image must be less than 10MB.");
            return;
        }
        const localUrl = URL.createObjectURL(file);
        setImagePreview({ file, localUrl, uploadedUrl: null, uploading: true, error: null });
        setError(null);
        // Reset file input so re-selecting same file works
        e.target.value = "";

        // Start uploading immediately
        api.uploadImage(file)
            .then((result) => {
                const uploadedUrl = result?.data?.url || result?.url;
                setImagePreview((prev) => {
                    // Only update if this is still the same file
                    if (!prev || prev.localUrl !== localUrl) return prev;
                    return { ...prev, uploadedUrl, uploading: false };
                });
            })
            .catch((err) => {
                console.error("[SalesiveWidget] Image upload failed", err);
                setImagePreview((prev) => {
                    if (!prev || prev.localUrl !== localUrl) return prev;
                    return { ...prev, uploading: false, error: "Upload failed" };
                });
            });
    }, []);

    const handleRemoveImage = useCallback(() => {
        if (imagePreview?.localUrl) {
            URL.revokeObjectURL(imagePreview.localUrl);
        }
        setImagePreview(null);
    }, [imagePreview]);

    // ─── Prefill handler ─────────────────────────────────────────────
    const handlePrefill = useCallback(
        ({ text, imageUrl } = {}) => {
            // Switch to current store conversation
            if (currentShop?._id && conversations.length) {
                const storeConvo = conversations.find(
                    (c) =>
                        String(c.shop?._id || c.shop?.id) ===
                        String(currentShop._id),
                );
                if (storeConvo) {
                    setSelectedConversationId(storeConvo.id);
                    setShowConversationList(false);
                }
            }

            // Prefill text
            if (typeof text === "string" && text.trim()) {
                setInputValue(text);
            }

            // Prefill image from URL (already uploaded / external)
            if (typeof imageUrl === "string" && imageUrl.trim()) {
                // Clear any previous preview
                if (imagePreview?.localUrl) {
                    URL.revokeObjectURL(imagePreview.localUrl);
                }
                setImagePreview({
                    file: null,
                    localUrl: imageUrl,
                    uploadedUrl: imageUrl,
                    uploading: false,
                    error: null,
                });
            }

            // Focus input after a tick so the chat is open
            setTimeout(() => inputRef.current?.focus(), 100);
        },
        [currentShop, conversations, imagePreview],
    );

    // Expose the prefill handler to the parent
    useEffect(() => {
        if (typeof onPrefillReady === "function") {
            onPrefillReady(handlePrefill);
        }
    }, [handlePrefill, onPrefillReady]);

    const handleKeyDown = (e) => {
        // Let product mention handle keyboard navigation first
        if (productMention.onKeyDown(e)) {
            e.preventDefault();
            return;
        }
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleSelectConversation = useCallback((conversationId) => {
        setSelectedConversationId(conversationId);
        setShowConversationList(false);
        persistConversationId(conversationId);
    }, []);

    const handleBackToList = useCallback(() => {
        setShowConversationList(true);
    }, []);

    const typingTimeoutRef = useRef(null);
    const isTypingRef = useRef(false);

    const MAX_CHARS = 1000;
    const [charLimitShake, setCharLimitShake] = useState(false);

    const handleInputChange = useCallback(
        (event) => {
            const raw = event.target.value;
            if (raw.length > MAX_CHARS) {
                // Reject excess — vibrate and shake
                if (navigator.vibrate) navigator.vibrate(50);
                setCharLimitShake(true);
                setTimeout(() => setCharLimitShake(false), 400);
            }
            const newValue = raw.slice(0, MAX_CHARS);
            setInputValue(newValue);

            // Trigger product mention detection
            productMention.onInputChange(newValue);

            if (!selectedConversationId) return;

            // Clear existing timeout
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }

            // check if input is empty
            if (!event.target.value.trim()) {
                if (isTypingRef.current) {
                    isTypingRef.current = false;
                    stopTyping(selectedConversationId);
                }
                return;
            }

            // Start typing if not already
            if (!isTypingRef.current) {
                isTypingRef.current = true;
                startTyping(selectedConversationId, {
                    message: event.target.value,
                });
            } else {
                // Send typing even if already started (throttle can be handled in useSocket or here if needed,
                // but user request implies they want "message" in payload, which changes.
                // Ideally we should send updates. But for now, let's just ensure startTyping is called with latest val
                // if we consider "typing" event as "activity".
                // However, startTyping usually implies "started".
                // If we want to send updates, we might need a separate 'updateTyping'.
                // Let's assume sending it once on start and maybe on debounce?
                // Actually, standard typing indicators don't stream content usually.
                // But payload has "message". Let's update it if possible.
                // Re-calling startTyping with new options might be fine if server handles it.
                startTyping(selectedConversationId, {
                    message: event.target.value,
                });
            }

            // Set timeout to stop typing
            typingTimeoutRef.current = setTimeout(() => {
                isTypingRef.current = false;
                stopTyping(selectedConversationId);
            }, 30000);
        },
        [selectedConversationId, startTyping, stopTyping],
    );

    const buildConversationFromMessage = useCallback((msg) => {
        console.log("[SalesiveWidget] buildConversationFromMessage", msg);
        const conversationId = getMessageConversationId(msg);
        if (!conversationId) return null;

        const lastMessageTimestamp =
            msg?.createdAt || msg?.timestamp || new Date();

        return {
            id: conversationId,
            title:
                msg?.conversation?.title ||
                msg?.shop?.name ||
                msg?.sender?.name ||
                "New Conversation",
            type: msg?.conversation?.type || "shop_support",
            lastMessage: {
                content: msg?.content ?? msg?.text ?? "",
                createdAt: lastMessageTimestamp,
            },
            updatedAt: lastMessageTimestamp,
            unreadCount: 0,
        };
    }, []);

    const selectedConversationIdRef = useRef(selectedConversationId);
    const isOpenRef = useRef(isOpen);
    const handleIncomingMessageRef = useRef(null);
    const processedMessagesRef = useRef(new Set());

    useEffect(() => {
        selectedConversationIdRef.current = selectedConversationId;
        isOpenRef.current = isOpen;
    }, [selectedConversationId, isOpen]);

    // Assign handler to ref - this updates on every render without re-registering listeners
    handleIncomingMessageRef.current = (msg) => {
        const msgId = msg?._id || msg?.id;
        if (msgId) {
            if (processedMessagesRef.current.has(String(msgId))) {
                return;
            }
            processedMessagesRef.current.add(String(msgId));
            if (processedMessagesRef.current.size > 500) {
                const arr = Array.from(processedMessagesRef.current);
                processedMessagesRef.current = new Set(
                    arr.slice(arr.length - 100),
                );
            }
        }

        const msgConversationId = getMessageConversationId(msg);
        const isCustomer = isCustomerMessage(msg);
        const currentSelectedId = selectedConversationIdRef.current;
        const currentIsOpen = isOpenRef.current;
        const isCurrentConversation =
            msgConversationId &&
            currentSelectedId &&
            String(msgConversationId) === String(currentSelectedId);

        // Agent activity: clear typing + mark online
        if (msgConversationId && !isCustomer) {
            setTypingUsers((prev) => {
                if (!prev[String(msgConversationId)]) return prev;
                const next = { ...prev };
                delete next[String(msgConversationId)];
                return next;
            });
            markConversationOnline(msgConversationId);
        }

        if (msgConversationId) {
            updateConversationsCache((previous) => {
                const index = previous.findIndex(
                    (c) => String(c.id) === String(msgConversationId),
                );

                const shouldIncrementUnread =
                    !currentIsOpen || !isCurrentConversation;

                const lastMessageTimestamp =
                    msg?.createdAt || msg?.timestamp || new Date();

                if (index > -1) {
                    const existing = previous[index];
                    const updated = {
                        ...existing,
                        lastMessage: {
                            ...(existing.lastMessage || {}),
                            content: msg?.content ?? msg?.text ?? "",
                            sender: msg?.sender || existing.lastMessage?.sender,
                            createdAt: lastMessageTimestamp,
                        },
                        updatedAt: lastMessageTimestamp,
                        unreadCount: shouldIncrementUnread
                            ? (Number(existing.unreadCount) || 0) + 1
                            : 0,
                    };
                    const copy = [...previous];
                    copy[index] = updated;
                    return copy;
                }

                const newConversation =
                    buildConversationFromMessage(msg) || undefined;

                if (!newConversation) {
                    return previous;
                }

                newConversation.unreadCount = shouldIncrementUnread ? 1 : 0;
                return [newConversation, ...previous];
            });
        }

        // Merge if it belongs to current open view
        if (isCurrentConversation) {
            mergeMessagesIntoCache([msg], currentSelectedId);
            if (currentIsOpen) {
                markConversationRead(currentSelectedId);
            }
        }

        // Play sound for agent messages when widget is closed, or user is on a different view
        if (!isCustomer) {
            const shouldPlaySound =
                !currentIsOpen || !isCurrentConversation;
            if (shouldPlaySound) {
                playNotificationSound();
            }
        }
    };

    // Socket setup effect - runs once when session is ready
    useEffect(() => {
        if (!isSessionReady) return undefined;

        connect();

        // Join the store room to receive broadcast messages
        const storeId =
            import.meta.env.VITE_SALESIVE_SHOP_ID ||
            localStorage.getItem("SALESIVE_SHOP_ID");

        if (storeId) {
            joinStore(storeId);
        }

        // Use wrapper function that calls ref to always get latest handler
        const wrappedHandler = (msg) => handleIncomingMessageRef.current?.(msg);

        const cleanupStoreMsg = onStoreMessageNew(wrappedHandler);
        const cleanupNew = onMessageNew(wrappedHandler);

        const cleanupTypingStart = onTypingStart((payload) => {
            const convId = getMessageConversationId(payload);
            if (!convId) return;
            // Customer widget: only show staff/AI typing. The events service
            // also echoes message:typing back to the sender; without a role
            // check that echo looks like "staff is typing" for the shopper.
            if (!payload.user || isShopperTypingUser(payload.user)) return;

            setTypingUsers((prev) => ({
                ...prev,
                [String(convId)]: payload.user,
            }));
            markConversationOnline(convId);
            // Auto-clear after 10s if no stop event received
            if (typingTimersRef.current[String(convId)]) {
                clearTimeout(typingTimersRef.current[String(convId)]);
            }
            typingTimersRef.current[String(convId)] = setTimeout(() => {
                setTypingUsers((prev) => {
                    const next = { ...prev };
                    delete next[String(convId)];
                    return next;
                });
            }, 10000);
        });

        const cleanupTypingStop = onTypingStop((payload) => {
            const convId = getMessageConversationId(payload);
            if (!convId) return;
            // Ignore our own (and other shoppers') stop events — we never
            // surfaced their start event as a staff bubble.
            if (payload.user && isShopperTypingUser(payload.user)) return;
            if (typingTimersRef.current[String(convId)]) {
                clearTimeout(typingTimersRef.current[String(convId)]);
                delete typingTimersRef.current[String(convId)];
            }
            setTypingUsers((prev) => {
                const next = { ...prev };
                delete next[String(convId)];
                return next;
            });
        });

        const cleanupSent = onMessageSent((msg) => {
            const msgConversationId = getMessageConversationId(msg);
            if (
                msgConversationId &&
                selectedConversationId &&
                String(msgConversationId) !== String(selectedConversationId)
            ) {
                return;
            }
            const normalized = normalizeMessage(msg);
            if (normalized) {
                replacePendingMessage(normalized);
            }
        });

        const cleanupHistory = onMessageHistory((payload) => {
            const payloadConversationId = getMessageConversationId(payload);
            if (
                payloadConversationId &&
                selectedConversationId &&
                String(payloadConversationId) !== String(selectedConversationId)
            ) {
                return;
            }
            mergeMessagesIntoCache(payload, payloadConversationId);
        });
        const cleanupRead = onMessageRead((payload) => {
            const senderInfo =
                payload.user ||
                (payload.readBy ? { _id: payload.readBy } : null);

            const isMe = senderInfo
                ? isCustomerMessage({ sender: senderInfo })
                : false;

            const payloadConversationId = getMessageConversationId(payload);
            const isCurrentConversation =
                payloadConversationId &&
                selectedConversationId &&
                String(payloadConversationId) ===
                    String(selectedConversationId);

            if (isMe) {
                // The current user (buyer) read messages — clear unread count
                updateConversationsCache((prev) =>
                    prev.map((c) =>
                        String(c.id) === String(payloadConversationId)
                            ? { ...c, unreadCount: 0 }
                            : c,
                    ),
                );
            } else {
                // The agent read our messages — mark buyer's messages as read (double-tick)
                markConversationOnline(payloadConversationId);
                // Update lastMessage read status on the conversation list
                updateConversationsCache((prev) =>
                    prev.map((c) => {
                        if (String(c.id) !== String(payloadConversationId))
                            return c;
                        const lm = c.lastMessage;
                        if (!lm) return c;
                        return {
                            ...c,
                            lastMessage: {
                                ...lm,
                                read: true,
                                readBy: [
                                    ...(lm.readBy || []),
                                    {
                                        user:
                                            senderInfo?._id || senderInfo?.id,
                                        readAt:
                                            payload.readAt || new Date(),
                                    },
                                ],
                            },
                        };
                    }),
                );
                if (isCurrentConversation) {
                    queryClient.setQueryData(
                        ["messages", selectedConversationId],
                        (prev = []) =>
                            prev.map((m) =>
                                m.type === "user"
                                    ? {
                                          ...m,
                                          read: true,
                                          readAt:
                                              payload.readAt || new Date(),
                                      }
                                    : m,
                            ),
                    );
                }
            }
        });
        const cleanupError = onMessageError((errPayload) => {
            console.error("[SalesiveSocket] message:error", errPayload);
            setError(
                errPayload?.message ||
                    "Something went wrong sending your message.",
            );
        });

        const cleanupMsgJoined = onMessageJoined((payload) => {
            // console.log("[SalesiveWidget] Joined conversation:", payload);
        });

        const cleanupStoreJoined = onStoreJoined((payload) => {
            // console.log("[SalesiveWidget] Joined store:", payload);
        });

        return () => {
            cleanupStoreMsg?.();
            cleanupNew?.();
            cleanupTypingStart?.();
            cleanupTypingStop?.();
            cleanupSent?.();
            cleanupHistory?.();
            cleanupRead?.();
            cleanupError?.();
            cleanupMsgJoined?.();
            cleanupStoreJoined?.();
            // Clear all typing timers
            Object.values(typingTimersRef.current).forEach(clearTimeout);
            typingTimersRef.current = {};
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSessionReady, selectedConversationId]);

    useEffect(() => {
        if (!isSessionReady) return;
        if (!selectedConversationId) return;

        joinConversation({ conversationId: selectedConversationId });
        fetchSocketHistory({
            conversationId: selectedConversationId,
            limit: 50,
        });

        if (isOpen) {
            updateConversationsCache((prev) =>
                prev.map((c) =>
                    String(c.id) === String(selectedConversationId)
                        ? { ...c, unreadCount: 0 }
                        : c,
                ),
            );
            markConversationRead(selectedConversationId);
        }
    }, [
        isSessionReady,
        selectedConversationId,
        isOpen,
        joinConversation,
        fetchSocketHistory,
        markConversationRead,
        updateConversationsCache,
    ]);

    const formatTime = (date) => {
        return date.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const isConversationEmpty =
        conversations.length === 0 ||
        (conversations.length === 1 &&
            !conversations[0].lastMessage?.content &&
            !conversations[0].lastMessagePreview);

    // Show AuthForm if session is ready, user is not fully logged in (Ghost), and hasn't skipped.
    const shouldShowAuth = isSessionReady && !isLoggedIn && !hasSkippedAuth;

    return (
        <AnimatePresence>
        {isOpen && (
        <motion.div
            key="chat-window"
            className={`
                tw-fixed tw-shadow-2xl tw-flex tw-flex-col tw-overflow-hidden
                tw-border
                tw-inset-0 sm:tw-inset-auto
                sm:tw-right-6 sm:tw-bottom-6
                tw-w-full sm:tw-w-[380px] md:tw-w-[380px] lg:tw-w-[400px]
                tw-h-full sm:tw-h-[580px] md:tw-h-[580px] lg:tw-h-[590px]
                tw-rounded-none sm:tw-rounded-2xl
                tw-z-[2147483647]
                ${
                    darkMode
                        ? "tw-bg-dark tw-border-gray-700"
                        : "tw-bg-white tw-border-gray-200"
                }
            `}
            initial={{ opacity: 0, scale: 0.92, y: 24, transformOrigin: "bottom right" }}
            animate={{ opacity: 1, scale: 1, y: 0, transformOrigin: "bottom right" }}
            exit={{ opacity: 0, scale: 0.92, y: 24, transformOrigin: "bottom right" }}
            transition={{
                type: "spring",
                stiffness: 380,
                damping: 28,
                mass: 0.8,
            }}
        >
            {/* Header */}
            <div className="tw-bg-primary tw-p-4 tw-flex tw-items-center tw-justify-between tw-gap-2 tw-sticky tw-top-0 tw-z-10 tw-shrink-0">
                <div className="tw-flex tw-items-center tw-gap-3 tw-flex-1 tw-min-w-0">
                    {!shouldShowAuth && !showConversationList && conversations.length > 1 && (
                        <button
                            onClick={handleBackToList}
                            className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-white/10 hover:tw-bg-white/20 tw-flex tw-items-center tw-justify-center tw-border-0 tw-cursor-pointer tw-transition-colors"
                            aria-label="Back to conversations"
                        >
                            <svg
                                className="tw-w-5 tw-h-5 tw-text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 19l-7-7 7-7"
                                />
                            </svg>
                        </button>
                    )}
                    {(() => {
                        const selectedConv = !showConversationList
                            ? conversations.find(
                                  (c) =>
                                      String(c.id) ===
                                      String(selectedConversationId),
                              )
                            : null;
                        const headerLogo = selectedConv?.shop?.logo || logo;
                        const shopDomain = selectedConv?.shop?.domain;
                        const isOtherStore =
                            selectedConv &&
                            currentShop?._id &&
                            String(selectedConv.shop?._id || selectedConv.shop?.id) !==
                                String(currentShop._id);
                        const storeUrl =
                            isOtherStore && shopDomain
                                ? shopDomain.includes("://")
                                    ? shopDomain
                                    : `https://${shopDomain}`
                                : null;
                        const headerTitle = shouldShowAuth
                            ? "Welcome"
                            : showConversationList
                              ? "Conversations"
                              : selectedConv?.shop?.name ||
                                getConversationLabel(selectedConv);
                        const headerSubtitle = shouldShowAuth
                            ? "Sign in to get started"
                            : showConversationList
                              ? "Select a store conversation"
                              : "We typically reply within minutes";

                        return (
                            <>
                                <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-white/20 tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-flex-shrink-0">
                                    {headerLogo ? (
                                        <img
                                            src={headerLogo}
                                            alt="Logo"
                                            className="tw-w-full tw-h-full tw-object-cover"
                                            onError={(e) => {
                                                const FALLBACK_LOGO =
                                                    "https://res.cloudinary.com/computer-geek/image/upload/v1757460903/Salesive/water_mark_jzbrjq.png";
                                                e.target.src = FALLBACK_LOGO;
                                            }}
                                        />
                                    ) : (
                                        <svg
                                            className="tw-w-6 tw-h-6 tw-text-white"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                                            />
                                        </svg>
                                    )}
                                </div>
                                <div className="tw-flex-1 tw-min-w-0">
                                    <div className="tw-flex tw-items-center tw-gap-1.5 tw-min-w-0">
                                        {storeUrl ? (
                                            <a
                                                href={storeUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="tw-text-white tw-font-semibold tw-text-base tw-m-0 tw-truncate tw-no-underline hover:tw-underline tw-cursor-pointer tw-leading-tight"
                                            >
                                                {headerTitle}
                                            </a>
                                        ) : (
                                            <h3 className="tw-text-white tw-font-semibold tw-text-base tw-m-0 tw-truncate tw-leading-tight">
                                                {headerTitle}
                                            </h3>
                                        )}
                                        {storeUrl && (
                                            <a
                                                href={storeUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="tw-text-white/70 hover:tw-text-white tw-transition-colors tw-flex-shrink-0"
                                                aria-label={`Visit ${headerTitle}`}
                                            >
                                                <svg
                                                    className="tw-w-3.5 tw-h-3.5"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                                    />
                                                </svg>
                                            </a>
                                        )}
                                    </div>
                                    <p className="tw-text-white/70 tw-text-xs tw-m-0 tw-truncate">
                                        {headerSubtitle}
                                    </p>
                                </div>
                            </>
                        );
                    })()}
                </div>
                <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
                    {!shouldShowAuth &&
                        !showConversationList &&
                        canStartCall && (
                            <button
                                onClick={handleStartCall}
                                className="tw-relative tw-w-8 tw-h-8 tw-rounded-full tw-bg-white/10 hover:tw-bg-white/20 tw-flex tw-items-center tw-justify-center tw-border-0 tw-cursor-pointer tw-transition-colors"
                                aria-label={
                                    isStoreOnline
                                        ? "Start voice call"
                                        : "Start voice call — the team is away, we'll call you back"
                                }
                                title={
                                    isStoreOnline
                                        ? "Start voice call"
                                        : "The team is away — call anyway and we'll get back to you"
                                }
                            >
                                <svg
                                    className="tw-w-[18px] tw-h-[18px] tw-text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                    />
                                </svg>
                                {/* Subtle presence dot: green = team online,
                                    grey = away (call still allowed). */}
                                <span
                                    className={`tw-absolute tw-bottom-0 tw-right-0 tw-w-2.5 tw-h-2.5 tw-rounded-full tw-border-2 tw-border-primary ${
                                        isStoreOnline
                                            ? "tw-bg-[#34C759]"
                                            : "tw-bg-gray-300"
                                    }`}
                                />
                            </button>
                        )}
                    <button
                        onClick={onClose}
                        className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-white/10 hover:tw-bg-white/20 tw-flex tw-items-center tw-justify-center tw-border-0 tw-cursor-pointer tw-transition-colors"
                        aria-label="Close chat"
                    >
                        <svg
                            className="tw-w-5 tw-h-5 tw-text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Voice call bar — compact, below the header, so the chat stays
                visible and usable during a call (outgoing / in-call / transient
                end states). */}
            {call && <CallOverlay call={call} darkMode={darkMode} />}

            {/* Conversation List View */}
            <AnimatePresence mode="wait">
                {shouldShowAuth && (
                    <motion.div
                        key="auth-form"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className={`tw-flex-1 tw-overflow-hidden ${
                            darkMode ? "tw-bg-dark" : "tw-bg-white"
                        }`}
                    >
                        <AuthForm
                            // OTP / Google sign-in only sets the httpOnly cookie — pull
                            // /auth/session so the mirror flips to user and this form hides.
                            onSuccess={() =>
                                syncAuthState({ forceServer: true })
                            }
                            onSkip={() => {
                                setHasSkippedAuth(true);
                                queryClient.invalidateQueries({
                                    queryKey: ["conversations"],
                                });
                            }}
                        />
                    </motion.div>
                )}

                {!shouldShowAuth && showConversationList && (
                    <ConversationList
                        conversations={conversations}
                        darkMode={darkMode}
                        isLoading={isConversationsLoading}
                        onSelect={handleSelectConversation}
                        typingUsers={typingUsers}
                        currentShopId={currentShop?._id}
                    />
                )}

                {/* Messages Area */}
                {!shouldShowAuth && !showConversationList && (
                    <motion.div
                        key="chat-view"
                        initial={{ x: 20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: 20, opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="tw-flex tw-flex-col tw-flex-1 tw-overflow-hidden"
                    >
                        <div
                            ref={messagesContainerCallbackRef}
                            className={`tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-4 tw-space-y-1 custom-scrollbar ${
                                darkMode ? "tw-bg-dark" : "tw-bg-[#f0f2f5]"
                            }`}
                            style={{ opacity: 0 }}
                        >
                            {!isSessionReady && (
                                <div className="tw-text-center tw-text-gray-400 tw-text-sm">
                                    Starting chat session…
                                </div>
                            )}

                            {isConversationsLoading && (
                                <div className="tw-text-center tw-text-gray-400 tw-text-sm">
                                    Loading conversations…
                                </div>
                            )}

                            {isSessionReady &&
                                !isConversationsLoading &&
                                !selectedConversationId && (
                                    <div className="tw-text-center tw-text-gray-400 tw-text-sm">
                                        No conversations available.
                                    </div>
                                )}

                            {isLoading && !hasLoadedHistory && (
                                <div className="tw-text-center tw-text-gray-400 tw-text-sm">
                                    Loading your conversation…
                                </div>
                            )}

                            {/* Not while someone is typing: Ola opens most threads with a
                                greeting, so the customer was told to start the
                                conversation and watched the store start it in the same
                                breath. The prompt is for an empty room, and the room is
                                no longer empty. */}
                            {!isConversationsLoading &&
                                !isLoading &&
                                selectedConversationId &&
                                messages.length === 0 &&
                                !typingUser && (
                                    <div className="tw-text-center tw-text-gray-400 tw-text-sm">
                                        Start the conversation and we’ll reply
                                        as soon as we can.
                                    </div>
                                )}

                            <AnimatePresence initial={false}>
                                {messages.map((message, msgIndex) => {
                                    const isUser = message.type === "user";
                                    const isImage = message.messageType === "image" && (message.attachment?.url || message.localImageUrl);

                                    // Use clientMessageId as stable key to prevent re-animation on update
                                    const stableKey =
                                        message.clientMessageId || message.id;

                                    // Date separator pill
                                    let datePill = null;
                                    const msgDate = new Date(message.timestamp);
                                    const msgDay = new Date(msgDate.getFullYear(), msgDate.getMonth(), msgDate.getDate());
                                    if (msgIndex === 0) {
                                        datePill = true;
                                    } else {
                                        const prevDate = new Date(messages[msgIndex - 1].timestamp);
                                        const prevDay = new Date(prevDate.getFullYear(), prevDate.getMonth(), prevDate.getDate());
                                        datePill = msgDay.getTime() !== prevDay.getTime();
                                    }

                                    const getDateLabel = (date) => {
                                        const now = new Date();
                                        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                                        const yesterday = new Date(today);
                                        yesterday.setDate(yesterday.getDate() - 1);
                                        const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                                        if (day.getTime() === today.getTime()) return "Today";
                                        if (day.getTime() === yesterday.getTime()) return "Yesterday";
                                        return date.toLocaleDateString([], { month: "short", day: "numeric" });
                                    };

                                    // System notes — "Ada joined the conversation",
                                    // "handed this conversation back to Ola" — render
                                    // as a centred pill, not a chat bubble. They are
                                    // ABOUT the thread, not FROM anyone, and the
                                    // customer deserves to know when the person they
                                    // are talking to changes.
                                    //
                                    // The sentence is composed server-side and arrives
                                    // in `text`. This widget is vendored into every
                                    // merchant theme, so wording that lived here would
                                    // need every theme republished to change.
                                    if (message.messageType === "system") {
                                        const systemEvent =
                                            message.metadata?.systemEvent;

                                        // Closing and reopening are states; everything
                                        // else (takeover, assignment, handoff) is a
                                        // handover between people.
                                        const systemIconPath =
                                            systemEvent === "conversation_closed"
                                                ? "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                : systemEvent ===
                                                    "conversation_reopened"
                                                  ? "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                                  : "M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4";

                                        return (
                                            <React.Fragment key={stableKey}>
                                                {datePill && (
                                                    <div className="tw-flex tw-justify-center tw-my-2">
                                                        <span
                                                            className={`tw-text-[11px] tw-font-medium tw-px-3 tw-py-0.5 tw-rounded-full tw-shadow-sm ${
                                                                darkMode
                                                                    ? "tw-bg-gray-800 tw-text-gray-400"
                                                                    : "tw-bg-white tw-text-gray-500"
                                                            }`}
                                                        >
                                                            {getDateLabel(msgDate)}
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="tw-flex tw-justify-center tw-my-1.5">
                                                    {/* One line, always. Wrapping is what
                                                        made this pill twice as tall as the
                                                        call logs it sits among — a long
                                                        name ("… handed this conversation
                                                        back to Ola") folded onto a second
                                                        row. Truncate instead; the full
                                                        sentence is on the title. */}
                                                    <span
                                                        title={message.text}
                                                        className={`tw-inline-flex tw-items-center tw-gap-1 tw-text-[10px] tw-font-medium tw-px-2.5 tw-py-0.5 tw-rounded-full tw-shadow-sm tw-max-w-[85%] tw-whitespace-nowrap ${
                                                            darkMode
                                                                ? "tw-bg-gray-800 tw-text-gray-400"
                                                                : "tw-bg-white tw-text-gray-500"
                                                        }`}
                                                    >
                                                        <svg
                                                            className="tw-w-2.5 tw-h-2.5 tw-shrink-0 tw-text-gray-400"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d={systemIconPath}
                                                            />
                                                        </svg>
                                                        {/* min-w-0 is what actually lets
                                                            truncate work inside a flex row */}
                                                        <span className="tw-truncate tw-min-w-0">
                                                            {message.text}
                                                        </span>
                                                        <span className="tw-opacity-60 tw-text-[9px] tw-shrink-0">
                                                            {formatTime(
                                                                message.timestamp,
                                                            )}
                                                        </span>
                                                    </span>
                                                </div>
                                            </React.Fragment>
                                        );
                                    }

                                    // Call log entries (type:"call") render as a
                                    // centered system pill, not a chat bubble.
                                    if (message.messageType === "call") {
                                        const callStatus =
                                            message.metadata?.callStatus;
                                        const isMissed =
                                            callStatus === "missed" ||
                                            callStatus === "rejected";
                                        const callLabel =
                                            message.text ||
                                            (callStatus === "ended"
                                                ? "Voice call"
                                                : callStatus === "rejected"
                                                  ? "Voice call declined"
                                                  : "Missed voice call");
                                        return (
                                            <React.Fragment key={stableKey}>
                                                {datePill && (
                                                    <div className="tw-flex tw-justify-center tw-my-2">
                                                        <span
                                                            className={`tw-text-[11px] tw-font-medium tw-px-3 tw-py-0.5 tw-rounded-full tw-shadow-sm ${
                                                                darkMode
                                                                    ? "tw-bg-gray-800 tw-text-gray-400"
                                                                    : "tw-bg-white tw-text-gray-500"
                                                            }`}
                                                        >
                                                            {getDateLabel(msgDate)}
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="tw-flex tw-justify-center tw-my-2">
                                                    <span
                                                        className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-font-medium tw-px-3 tw-py-1 tw-rounded-full tw-shadow-sm ${
                                                            darkMode
                                                                ? "tw-bg-gray-800 tw-text-gray-400"
                                                                : "tw-bg-white tw-text-gray-500"
                                                        }`}
                                                    >
                                                        <svg
                                                            className={`tw-w-3 tw-h-3 ${
                                                                isMissed
                                                                    ? "tw-text-red-400"
                                                                    : "tw-text-emerald-500"
                                                            }`}
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                                            />
                                                        </svg>
                                                        <span>{callLabel}</span>
                                                        <span className="tw-opacity-60 tw-text-[10px]">
                                                            {formatTime(
                                                                message.timestamp,
                                                            )}
                                                        </span>
                                                    </span>
                                                </div>
                                            </React.Fragment>
                                        );
                                    }

                                    return (
                                        <React.Fragment key={stableKey}>
                                        {datePill && (
                                            <div className="tw-flex tw-justify-center tw-my-2">
                                                <span
                                                    className={`tw-text-[11px] tw-font-medium tw-px-3 tw-py-0.5 tw-rounded-full tw-shadow-sm ${
                                                        darkMode
                                                            ? "tw-bg-gray-800 tw-text-gray-400"
                                                            : "tw-bg-white tw-text-gray-500"
                                                    }`}
                                                >
                                                    {getDateLabel(msgDate)}
                                                </span>
                                            </div>
                                        )}
                                        <motion.div
                                            key={stableKey}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{
                                                opacity: message.pending
                                                    ? 0.7
                                                    : 1,
                                                y: 0,
                                            }}
                                            transition={{
                                                type: "spring",
                                                stiffness: 500,
                                                damping: 30,
                                            }}
                                            className={`tw-relative tw-flex tw-w-full tw-mb-1 ${
                                                isUser
                                                    ? "tw-justify-end"
                                                    : "tw-justify-start"
                                            }`}
                                        >
                                            {/* Bubble tail ("ear") — sibling of the bubble because the
                                                bubble root clips its own overflow. */}
                                            {isUser && !isImage && (
                                                <div className="tw-absolute tw-top-0 tw-right-[-8px] tw-w-0 tw-h-0 tw-border-solid tw-border-t-0 tw-border-r-0 tw-border-l-[9px] tw-border-l-primary tw-border-b-[10px] tw-border-b-transparent" />
                                            )}
                                            {/* Sender avatar for non-user messages */}
                                            {/* {!isUser && (
                                                <div className="tw-flex-shrink-0 tw-w-7 tw-h-7 tw-rounded-full tw-overflow-hidden tw-mr-2 tw-mt-auto tw-mb-0.5">
                                                    {message.senderAvatar ? (
                                                        <img
                                                            src={message.senderAvatar}
                                                            alt={message.senderName || ""}
                                                            className="tw-w-full tw-h-full tw-object-cover"
                                                            onError={(e) => {
                                                                e.target.style.display = "none";
                                                                e.target.parentElement.classList.add(
                                                                    darkMode ? "tw-bg-gray-700" : "tw-bg-gray-200"
                                                                );
                                                            }}
                                                        />
                                                    ) : (
                                                        <div
                                                            className={`tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-text-[11px] tw-font-semibold ${
                                                                darkMode
                                                                    ? "tw-bg-gray-700 tw-text-gray-400"
                                                                    : "tw-bg-gray-200 tw-text-gray-500"
                                                            }`}
                                                        >
                                                            {(message.senderName || "?").charAt(0).toUpperCase()}
                                                        </div>
                                                    )}
                                                </div>
                                            )} */}
                                            {(() => {
                                                const isRich = ["product_card_list", "copiable_text", "button_link"].includes(message.messageType);
                                                const isPlainText = !isImage && !isRich;

                                                // Timestamp element (reused below)
                                                const timeStamp = (
                                                    <span
                                                        className={`tw-text-[10px] tw-font-sans tw-flex tw-items-center tw-gap-1 tw-select-none tw-whitespace-nowrap ${
                                                            isUser
                                                                ? "tw-text-white/70"
                                                                : darkMode
                                                                  ? "tw-text-gray-500"
                                                                  : "tw-text-gray-400"
                                                        }`}
                                                    >
                                                        <span>{formatTime(message.timestamp)}</span>
                                                        {message.pending && <em className="tw-text-[9px]">...</em>}
                                                        {!message.pending && isUser && (
                                                            <span className="tw-flex tw-items-center">
                                                                {message.read ? (
                                                                    <IoCheckmarkDoneSharp className="tw-text-emerald-300" size={15} />
                                                                ) : (
                                                                    <IoCheckmarkSharp className="tw-text-white/70" size={15} />
                                                                )}
                                                            </span>
                                                        )}
                                                    </span>
                                                );

                                                // Bubble sizing classes
                                                const sizeClasses = isImage
                                                    ? "tw-w-[250px] tw-max-w-[calc(100%-2rem)] tw-overflow-hidden"
                                                    : isRich
                                                      ? "tw-w-full md:tw-w-auto tw-max-w-[calc(100%-2rem)] sm:tw-max-w-[calc(100%-1.25rem)] md:tw-max-w-[80%]"
                                                      : "tw-w-fit tw-min-w-0 tw-max-w-[calc(100%-2rem)] sm:tw-max-w-[calc(100%-1.25rem)] md:tw-max-w-[80%]";

                                                return (
                                                    <div
                                                        className={`tw-relative ${sizeClasses} tw-shadow-[0_1px_2px_rgba(0,0,0,0.05)] ${
                                                            isImage ? "" : "tw-px-3.5 tw-py-2.5"
                                                        } ${
                                                            isUser
                                                                ? `tw-bg-primary tw-text-white tw-rounded-2xl ${isImage ? "tw-rounded-tr-[4px]" : "tw-rounded-tr-none"}`
                                                                : darkMode
                                                                  ? "tw-bg-gray-800 tw-text-gray-100 tw-rounded-2xl tw-rounded-tl-[4px] tw-border tw-border-gray-700"
                                                                  : "tw-bg-white tw-text-gray-800 tw-rounded-2xl tw-rounded-tl-[4px]"
                                                        }`}
                                                    >
                                                        {!isUser && message.senderName && (
                                                            <div className={`tw-text-[10px] tw-font-semibold tw-mb-0.5 tw-text-left tw-w-full ${
                                                                darkMode ? "tw-text-gray-300" : "tw-text-primary"
                                                            } ${
                                                                isImage ? "tw-px-3.5 tw-pt-2.5" : ""
                                                            }`}>
                                                                {message.senderName}
                                                            </div>
                                                        )}

                                                        {/* Image message */}
                                                        {isImage && (
                                                            <div
                                                                className="tw-relative tw-cursor-pointer tw-overflow-hidden tw-w-full tw-aspect-square"
                                                                onClick={() => {
                                                                    const imgUrl = message.attachment?.url || message.localImageUrl;
                                                                    if (!imgUrl) return;
                                                                    const idx = chatImages.findIndex((img) => img.src === imgUrl);
                                                                    setViewerImageIndex(idx >= 0 ? idx : 0);
                                                                }}
                                                            >
                                                                <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-z-[1] tw-pointer-events-none" data-img-spinner="true">
                                                                    <div className={`tw-w-6 tw-h-6 tw-border-2 tw-rounded-full tw-animate-spin ${
                                                                        isUser
                                                                            ? "tw-border-white/30 tw-border-t-white"
                                                                            : "tw-border-primary/30 tw-border-t-primary"
                                                                    }`} />
                                                                </div>
                                                                <img
                                                                    src={message.attachment?.url || message.localImageUrl}
                                                                    alt={message.attachment?.name || "Image"}
                                                                    className="tw-w-full tw-h-full tw-object-cover tw-block tw-relative tw-z-[2]"
                                                                    loading="lazy"
                                                                    onLoad={(e) => {
                                                                        const spinner = e.target.parentElement?.querySelector("[data-img-spinner]");
                                                                        if (spinner) spinner.style.display = "none";
                                                                    }}
                                                                />
                                                            </div>
                                                        )}

                                                        {/* Plain text — inline timestamp (WhatsApp style) */}
                                                        {isPlainText && message.text && (
                                                            <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-x-3 tw-gap-y-0">
                                                                <div className="tw-inline tw-min-w-0">
                                                                    <MessageMarkdown
                                                                        content={message.text}
                                                                        isUser={isUser}
                                                                        darkMode={darkMode}
                                                                        inline
                                                                    />
                                                                </div>
                                                                <span className="tw-ml-auto">{timeStamp}</span>
                                                            </div>
                                                        )}

                                                        {/* Image caption text */}
                                                        {isImage && message.text && (
                                                            <div className="tw-w-full tw-min-w-0 tw-text-left tw-px-3.5 tw-py-1.5">
                                                                <MessageMarkdown
                                                                    content={message.text}
                                                                    isUser={isUser}
                                                                    darkMode={darkMode}
                                                                />
                                                            </div>
                                                        )}

                                                        {/* Rich content text */}
                                                        {isRich && message.text && (
                                                            <div className="tw-w-full tw-min-w-0 tw-text-left">
                                                                <MessageMarkdown
                                                                    content={message.text}
                                                                    isUser={isUser}
                                                                    darkMode={darkMode}
                                                                />
                                                            </div>
                                                        )}

                                                        {/* Rich message types */}
                                                        {message.messageType === "product_card_list" && message.metadata?.products && (
                                                            <ProductCardList
                                                                products={message.metadata.products}
                                                                darkMode={darkMode}
                                                                onMentionProduct={(product) => {
                                                                    const name = product.name || "this product";
                                                                    setInputValue((prev) => {
                                                                        const separator = prev.trim() ? " " : "";
                                                                        return name + " " + separator + prev;
                                                                    });
                                                                    setTimeout(() => {
                                                                        const el = inputRef.current;
                                                                        if (!el) return;
                                                                        el.focus();
                                                                        el.setSelectionRange(0, 0);
                                                                    }, 50);
                                                                }}
                                                            />
                                                        )}
                                                        {message.messageType === "copiable_text" && message.metadata && (
                                                            <CopiableText metadata={message.metadata} darkMode={darkMode} />
                                                        )}
                                                        {message.messageType === "button_link" && message.metadata && (
                                                            <ButtonLink metadata={message.metadata} darkMode={darkMode} />
                                                        )}

                                                        {/* Block timestamp for image / rich messages */}
                                                        {!isPlainText && (
                                                            <div className={`tw-flex tw-items-center tw-justify-end tw-gap-1 tw-mt-1 ${
                                                                isImage ? "tw-px-3.5 tw-pb-1.5" : ""
                                                            }`}>
                                                                {timeStamp}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </motion.div>
                                        </React.Fragment>
                                    );
                                })}
                            </AnimatePresence>

                            {/* Typing Indicator */}
                            {typingUser && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 10 }}
                                    transition={{
                                        type: "spring",
                                        stiffness: 500,
                                        damping: 30,
                                    }}
                                    className="tw-flex tw-w-full tw-mb-1 tw-justify-start"
                                >
                                    <div
                                        className={`tw-relative tw-inline-block tw-max-w-[85%] tw-px-4 tw-py-2.5 tw-shadow-[0_1px_2px_rgba(0,0,0,0.05)] tw-rounded-2xl tw-rounded-tl-[4px] ${
                                            darkMode
                                                ? "tw-bg-gray-800 tw-text-gray-100 tw-border tw-border-gray-700"
                                                : "tw-bg-white tw-text-gray-800"
                                        }`}
                                    >
                                        <div className="tw-flex tw-items-center tw-gap-2">
                                            <div className="tw-flex tw-space-x-1 tw-py-1">
                                                <div
                                                    className="tw-w-1.5 tw-h-1.5 tw-bg-gray-400 tw-rounded-full tw-animate-bounce"
                                                    style={{
                                                        animationDelay: "0ms",
                                                    }}
                                                ></div>
                                                <div
                                                    className="tw-w-1.5 tw-h-1.5 tw-bg-gray-400 tw-rounded-full tw-animate-bounce"
                                                    style={{
                                                        animationDelay: "150ms",
                                                    }}
                                                ></div>
                                                <div
                                                    className="tw-w-1.5 tw-h-1.5 tw-bg-gray-400 tw-rounded-full tw-animate-bounce"
                                                    style={{
                                                        animationDelay: "300ms",
                                                    }}
                                                ></div>
                                            </div>
                                            <span className="tw-text-[11px] tw-text-gray-500 tw-ml-1">
                                                {typingUser.name || "Someone"}{" "}
                                                is typing...
                                            </span>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input Area */}
                        <div
                            className={`tw-flex-none tw-flex tw-flex-col tw-w-full tw-pt-2 tw-px-2.5 tw-pb-1.5 tw-border-t tw-sticky tw-bottom-0 tw-shrink-0 ${
                                darkMode
                                    ? "tw-bg-dark tw-border-gray-700"
                                    : "tw-bg-white tw-border-[#EEEEEE]"
                            }`}
                        >
                            {/* Product mention dropdown */}
                            <ProductMentionDropdown
                                isOpen={productMention.isOpen}
                                results={productMention.results}
                                isLoading={productMention.isLoading}
                                query={productMention.query}
                                activeIndex={productMention.activeIndex}
                                onSelect={productMention.selectProduct}
                                darkMode={darkMode}
                            />
                            {error && (
                                <div className="tw-mb-1.5 tw-text-[11px] tw-text-red-500 tw-text-center tw-w-full tw-leading-tight">
                                    {error}
                                </div>
                            )}
                            {/* Image preview */}
                            {imagePreview && (
                                <div className="tw-mb-1.5 tw-px-1">
                                    <div className={`tw-relative tw-inline-block tw-rounded-lg tw-overflow-hidden tw-border ${
                                        darkMode ? "tw-border-gray-700" : "tw-border-gray-200"
                                    }`}>
                                        <img
                                            src={imagePreview.localUrl}
                                            alt="Preview"
                                            className="tw-max-h-[120px] tw-max-w-[160px] tw-object-cover tw-block"
                                        />
                                        <button
                                            onClick={handleRemoveImage}
                                            className="tw-absolute tw-top-1 tw-right-1 tw-w-5 tw-h-5 tw-rounded-full tw-bg-black/60 tw-text-white tw-border-0 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-p-0"
                                            aria-label="Remove image"
                                        >
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                                                <path d="M18 6L6 18M6 6l12 12" />
                                            </svg>
                                        </button>
                                        {imagePreview.uploading && (
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    inset: 0,
                                                    background: "rgba(0,0,0,0.5)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    zIndex: 2,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: 28,
                                                        height: 28,
                                                        border: "3px solid rgba(255,255,255,0.3)",
                                                        borderTopColor: "#fff",
                                                        borderRadius: "50%",
                                                        animation: "tw-spin 1s linear infinite",
                                                    }}
                                                />
                                            </div>
                                        )}
                                        {imagePreview.uploadedUrl && !imagePreview.uploading && (
                                            <div className="tw-absolute tw-bottom-1 tw-left-1 tw-w-5 tw-h-5 tw-rounded-full tw-bg-emerald-500 tw-flex tw-items-center tw-justify-center">
                                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M20 6L9 17l-5-5" />
                                                </svg>
                                            </div>
                                        )}
                                        {imagePreview.error && (
                                            <div className="tw-absolute tw-inset-0 tw-bg-black/40 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1">
                                                <span className="tw-text-[10px] tw-text-white tw-font-medium">Upload failed</span>
                                                <button
                                                    onClick={() => {
                                                        const file = imagePreview.file;
                                                        const localUrl = imagePreview.localUrl;
                                                        setImagePreview((prev) => ({ ...prev, uploading: true, error: null }));
                                                        api.uploadImage(file)
                                                            .then((result) => {
                                                                const uploadedUrl = result?.data?.url || result?.url;
                                                                setImagePreview((prev) => {
                                                                    if (!prev || prev.localUrl !== localUrl) return prev;
                                                                    return { ...prev, uploadedUrl, uploading: false };
                                                                });
                                                            })
                                                            .catch(() => {
                                                                setImagePreview((prev) => {
                                                                    if (!prev || prev.localUrl !== localUrl) return prev;
                                                                    return { ...prev, uploading: false, error: "Upload failed" };
                                                                });
                                                            });
                                                    }}
                                                    className="tw-text-[10px] tw-text-white tw-underline tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0"
                                                >
                                                    Retry
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleImageSelect}
                                className="tw-hidden"
                            />
                            <div className="tw-flex tw-items-end tw-w-full tw-gap-2">
                                <div
                                    className={`tw-flex-1 tw-rounded-xl tw-flex tw-items-end tw-border ${
                                        darkMode
                                            ? "tw-bg-gray-800 tw-border-gray-700 focus-within:tw-border-primary/40 focus-within:tw-ring-1 focus-within:tw-ring-primary/20"
                                            : "tw-bg-gray-50 tw-border-gray-200 focus-within:tw-bg-white focus-within:tw-border-primary/40 focus-within:tw-ring-1 focus-within:tw-ring-primary/20"
                                    }`}
                                    style={{
                                        transition:
                                            "border-color 0.25s ease, box-shadow 0.25s ease, background-color 0.2s ease",
                                    }}
                                >
                                    {/* Image attach button */}
                                    <div
                                        className={`tw-flex-shrink-0 tw-w-[36px] tw-h-[38px] tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-transition-colors ${
                                            darkMode
                                                ? "tw-text-gray-400 hover:tw-text-gray-200"
                                                : "tw-text-gray-400 hover:tw-text-gray-600"
                                        }`}
                                        onClick={() => fileInputRef.current?.click()}
                                        onMouseDown={(e) => e.preventDefault()}
                                        aria-label="Attach image"
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                            <circle cx="8.5" cy="8.5" r="1.5" />
                                            <path d="M21 15l-5-5L5 21" />
                                        </svg>
                                    </div>
                                    <textarea
                                        ref={inputRef}
                                        value={inputValue}
                                        onChange={handleInputChange}
                                        onKeyDown={handleKeyDown}
                                        placeholder={isCurrentShopConversation ? "Use @ to mention products" : "Type a message…"}
                                        rows={1}
                                        className={`
                                        tw-flex-1 tw-resize-none tw-border-0 tw-bg-transparent
                                        tw-pl-0 tw-pr-3.5 tw-py-2.5 tw-text-[13px] tw-leading-[1.45]
                                        tw-outline-none
                                        tw-font-manrope no-scrollbar
                                        ${
                                            darkMode
                                                ? "tw-text-gray-100 placeholder:tw-text-gray-500"
                                                : "tw-text-gray-800 placeholder:tw-text-gray-400"
                                        }
                                        `}
                                        style={{
                                            minHeight: "38px",
                                            maxHeight: "100px",
                                        }}
                                        maxLength={MAX_CHARS}
                                    />
                                    {inputValue.length > 900 && (
                                        <svg
                                            className="tw-flex-shrink-0 tw-self-end tw-mb-2 tw-mr-1"
                                            style={{
                                                transform: charLimitShake ? "translateX(-3px)" : "translateX(0)",
                                                transition: "transform 0.08s ease",
                                            }}
                                            ref={(el) => {
                                                if (el && charLimitShake) {
                                                    const seq = [3, -3, 2, -2, 1, -1, 0];
                                                    seq.forEach((v, i) => {
                                                        setTimeout(() => {
                                                            el.style.transform = `translateX(${v}px)`;
                                                        }, i * 50);
                                                    });
                                                }
                                            }}
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                        >
                                            <circle
                                                cx="10"
                                                cy="10"
                                                r="8"
                                                fill="none"
                                                stroke="rgb(var(--salesive-primary) / 0.15)"
                                                strokeWidth="2"
                                            />
                                            <circle
                                                cx="10"
                                                cy="10"
                                                r="8"
                                                fill="none"
                                                stroke={
                                                    inputValue.length >= MAX_CHARS
                                                        ? "#ef4444"
                                                        : inputValue.length > 950
                                                          ? "#f59e0b"
                                                          : "rgb(var(--salesive-primary))"
                                                }
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeDasharray={2 * Math.PI * 8}
                                                strokeDashoffset={2 * Math.PI * 8 * (1 - (inputValue.length - 900) / (MAX_CHARS - 900))}
                                                style={{
                                                    transform: "rotate(-90deg)",
                                                    transformOrigin: "center",
                                                    transition: "stroke-dashoffset 0.15s ease, stroke 0.15s ease",
                                                }}
                                            />
                                        </svg>
                                    )}
                                </div>
                                <div
                                    className={`tw-flex-shrink-0 tw-w-[36px] tw-h-[36px] tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-[1px] ${
                                        canSend
                                            ? "tw-bg-primary tw-text-white hover:tw-bg-primary/90 tw-shadow-sm tw-cursor-pointer"
                                            : darkMode
                                              ? "tw-bg-gray-700 tw-text-gray-500 tw-cursor-not-allowed"
                                              : "tw-bg-gray-200 tw-text-gray-400 tw-cursor-not-allowed"
                                    }`}
                                    style={{
                                        transition:
                                            "background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease",
                                        transform: canSend
                                            ? "scale(1)"
                                            : "scale(0.92)",
                                    }}
                                    onClick={canSend ? handleSend : undefined}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onTouchStart={(e) => e.preventDefault()}
                                    aria-label="Send message"
                                >
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M2.01 21L23 12L2.01 3L2 10l15 2l-15 2z"
                                            fill="currentColor"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                        <div
                            className={`tw-p-0 tw-text-center tw-flex tw-items-center tw-justify-center ${
                                darkMode
                                    ? "tw-text-gray-500"
                                    : "tw-text-gray-400"
                            }`}
                        >
                            <p className="tw-m-0 tw-mb-2 tw-text-xs">
                                Powered by{" "}
                                <a
                                    href="https://salesive.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="tw-text-primary hover:tw-text-primary active:tw-text-primary tw-outline-none tw-decoration-none"
                                    style={{
                                        textDecoration: "none",
                                    }}
                                >
                                    Salesive
                                </a>
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Image Viewer */}
            <ImageViewer
                isOpen={viewerImageIndex >= 0}
                images={chatImages}
                currentIndex={viewerImageIndex}
                onNavigate={setViewerImageIndex}
                onClose={() => setViewerImageIndex(-1)}
                darkMode={darkMode}
            />
        </motion.div>
        )}
        </AnimatePresence>
    );
}

/**
 * Simple bot response generator for demo purposes
 */
function getBotResponse(userMessage) {
    const lowerMessage = userMessage.toLowerCase();

    if (lowerMessage.includes("hello") || lowerMessage.includes("hi")) {
        return "Hi there! I'm here to help. What can I assist you with today?";
    }
    if (lowerMessage.includes("help")) {
        return "I'd be happy to help! Please describe your issue and I'll do my best to assist you.";
    }
    if (lowerMessage.includes("price") || lowerMessage.includes("cost")) {
        return "For pricing information, please visit our pricing page or let me connect you with our sales team.";
    }
    if (lowerMessage.includes("thank")) {
        return "You're welcome! Is there anything else I can help you with?";
    }
    if (lowerMessage.includes("bye") || lowerMessage.includes("goodbye")) {
        return "Goodbye! Feel free to reach out anytime you need assistance. Have a great day! 👋";
    }

    return "Thanks for your message! A support agent will review this and get back to you shortly. In the meantime, is there anything specific I can help you with?";
}

export default React.memo(ChatWindow);
